import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Canal } from '@libs/entity-lib';
import { commonEnvironment } from '@libs/shared/util-environments';
import { EMPTY, Observable, timer } from 'rxjs';
import { catchError, distinctUntilChanged, switchMap } from 'rxjs/operators';

/** Bloqueio de pagamento via PIX — independente do cartão. */
export const MAINTENANCE_PAYMENT_PIX = 'PAYMENT_PIX' as const;
/** Bloqueio de pagamento via cartão de crédito — independente do PIX. */
export const MAINTENANCE_PAYMENT_CARD = 'PAYMENT_CARD' as const;

/**
 * Canais de manutenção. Apps (ATHLETE/SPORT_CENTER) entram em manutenção com
 * redirect + countdown; PAYMENT_PIX/PAYMENT_CARD são bloqueios sem expiração,
 * lidos no checkout (por método, independentes entre si).
 */
export type MaintenanceChannel =
  | Canal
  | typeof MAINTENANCE_PAYMENT_PIX
  | typeof MAINTENANCE_PAYMENT_CARD;

export interface MaintenanceStatus {
  active: boolean;
  message: string;
  expiresAt: string | null;
  updatedAt?: string | null;
}

export interface MaintenanceRequest {
  active: boolean;
  message: string;
  /** Duração "HH:MM" — backend calcula expiresAt. Omitido = sem expiração (pagamento). */
  time?: string;
}

/**
 * Status de manutenção via REST (nosso backend) — substitui o Firestore.
 * Leitura pública (`/public/maintenance/{canal}`); escrita (`/api/maintenance/{canal}`)
 * restrita ao admin. A expiração é tratada no servidor, então o cliente nunca escreve.
 */
@Injectable({ providedIn: 'root' })
export class MaintenanceApiService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  /** Intervalo do polling que repõe o comportamento "ao vivo" do Firestore. */
  private readonly POLL_INTERVAL_MS = 30_000;

  /** Lê o status atual de um canal (uma vez). */
  getStatus(canal: MaintenanceChannel): Observable<MaintenanceStatus> {
    return this.http.get<MaintenanceStatus>(this.statusUrl(canal));
  }

  /**
   * Observa o status de um canal em intervalos fixos (polling) — substitui o
   * realtime do Firestore. Um poll que falha é ignorado (não muda o estado nem
   * derruba o stream). Só emite quando `active`/`expiresAt` mudam.
   */
  watch(canal: MaintenanceChannel): Observable<MaintenanceStatus> {
    return timer(0, this.POLL_INTERVAL_MS).pipe(
      switchMap(() => this.getStatus(canal).pipe(catchError(() => EMPTY))),
      distinctUntilChanged(
        (a, b) => a.active === b.active && a.expiresAt === b.expiresAt,
      ),
    );
  }

  /**
   * Liga o gate de manutenção no boot: observa o status e mantém o usuário em
   * sincronia — entra em `/maintenance/:canal` quando ativa e volta para a home
   * quando o admin desliga. Ambos os sentidos "ao vivo" via polling.
   */
  checkMaintenance(canal: MaintenanceChannel): void {
    this.watch(canal).subscribe((status) => {
      const onMaintenancePage = this.router.url.includes(`/maintenance/${canal}`);
      if (status.active && !onMaintenancePage) {
        this.router.navigate([`/maintenance/${canal}`]);
      } else if (!status.active && onMaintenancePage) {
        this.router.navigate(['/']);
      }
    });
  }

  /** Liga/atualiza a manutenção de um canal (admin). */
  setMaintenance(
    canal: MaintenanceChannel,
    request: MaintenanceRequest,
  ): Observable<MaintenanceStatus> {
    return this.http.put<MaintenanceStatus>(this.adminUrl(canal), request);
  }

  /** Desliga a manutenção de um canal (admin). */
  clear(canal: MaintenanceChannel): Observable<MaintenanceStatus> {
    return this.http.delete<MaintenanceStatus>(this.adminUrl(canal));
  }

  /**
   * Resolve o backend do canal. Apps usam o próprio baseUrl; PAYMENT (global)
   * cai no baseUrl do athlete — todos os apps apontam para o mesmo backend por
   * ambiente, então o store de manutenção é compartilhado.
   */
  private baseUrl(canal: MaintenanceChannel): string {
    const isPayment =
      canal === MAINTENANCE_PAYMENT_PIX || canal === MAINTENANCE_PAYMENT_CARD;
    const key = isPayment ? Canal.ATHLETE : canal;
    return commonEnvironment.apps[key].baseUrl;
  }

  private statusUrl(canal: MaintenanceChannel): string {
    return this.baseUrl(canal) + commonEnvironment.paths.maintenanceStatus.replace('{canal}', canal);
  }

  private adminUrl(canal: MaintenanceChannel): string {
    return this.baseUrl(canal) + commonEnvironment.paths.maintenanceAdmin.replace('{canal}', canal);
  }
}
